"use client";
import Card from "@/components/ui/Card";
import React from "react";

function TermsAndConditions() {
  return (
    <Card
      title={<h1 className="text-3xl font-bold mb-2">Terms of Service</h1>}
      className="bg-gray-100 container px-6 md:px-12 py-8 w-full mb-6"
    >
      <p className="text-sm text-gray-600 mb-4">Last updated: May 2026</p>

      <h2 className="text-2xl font-bold mb-2">1. Agreement to Terms</h2>
      <p className="mb-4">
        Welcome to Eyam. These Terms of Service ("Terms") govern your access to
        and use of the Eyam platform, including our website, mobile applications,
        and all related services (collectively, the "Platform"). By accessing or
        using Eyam, you agree to be bound by these Terms.
      </p>
      <p className="mb-4">
        If you do not agree to these Terms, you may not access or use the
        Platform. We reserve the right to modify these Terms at any time. Your
        continued use of the Platform after any changes constitutes acceptance of
        the modified Terms.
      </p>

      <h2 className="text-2xl font-bold mb-2">2. Platform Description</h2>
      <p className="mb-2">
        Eyam is a unified marketing platform that connects content creators
        ("Creators") with businesses ("Businesses") for authentic content
        creation and distribution opportunities. The Platform facilitates:
      </p>
      <ul className="list-disc list-inside mb-4 space-y-1">
        <li>
          Creator Campaigns: View-based compensation where Creators earn payouts
          based on verified views, referrals, and engagement (R20 per 1,000
          views, R100 per successful referral, plus bonuses for high-performing
          content)
        </li>
        <li>
          Business Campaigns: Unified marketing solutions with a 40/60 budget
          allocation model (40% funds creator content creation, 60% funds
          verified distribution)
        </li>
        <li>
          Discovery Listings: Free and paid business directory listings that
          connect Businesses with verified South African customers
        </li>
        <li>
          Content submission, review, approval, unified tracking (views, clicks,
          actions), and payout management
        </li>
      </ul>

      <h2 className="text-2xl font-bold mb-2">3. Account Registration</h2>
      <h3 className="text-xl font-semibold mb-2">3.1 Eligibility</h3>
      <p className="mb-4">
        You must be at least 18 years old to use Eyam. By registering, you
        represent and warrant that you meet this age requirement and have the
        legal capacity to enter into these Terms in accordance with South African
        law.
      </p>
      <h3 className="text-xl font-semibold mb-2">3.2 Account Information</h3>
      <p className="mb-4">
        You must provide accurate, current, and complete information during
        registration and keep your account information updated. This includes
        valid South African identification for KYC (Know Your Customer)
        verification. You are responsible for maintaining the confidentiality of
        your account credentials and for all activities that occur under your
        account.
      </p>
      <h3 className="text-xl font-semibold mb-2">3.3 Acceptance During Registration</h3>
      <p className="mb-4">
        By creating an Eyam account—whether you register with your email address
        and password, register with Google, or use another registration method we
        make available—you confirm that you have read and accept these Terms and
        our Privacy Policy. If you do not accept both documents, you must not
        create or use an Eyam account.
      </p>
      <h3 className="text-xl font-semibold mb-2">3.4 Google Registration and Sign-In</h3>
      <p className="mb-4">
        If you choose to register or sign in with Google, you authorize Google to
        provide Eyam with the account information required to authenticate you,
        including your verified email address, first name, and surname. Eyam may
        use the verified email address to create a new account or link you to an
        existing Eyam account with the same email address. Your use of Google&apos;s
        services remains subject to Google&apos;s own terms and privacy policy.
      </p>
      <h3 className="text-xl font-semibold mb-2">3.5 Account Security</h3>
      <p className="mb-4">
        You must immediately notify us of any unauthorized use of your account or
        any other breach of security. We are not liable for any loss or damage
        arising from your failure to protect your account credentials.
      </p>
      <h3 className="text-xl font-semibold mb-2">3.6 KYC Verification</h3>
      <p className="mb-2">
        To receive payouts or launch campaigns, you must complete our KYC
        verification process, which may include:
      </p>
      <ul className="list-disc list-inside mb-4 space-y-1">
        <li>South African ID number validation</li>
        <li>Phone number verification (OTP)</li>
        <li>Bank account details for payouts</li>
        <li>Business registration documents (for Business accounts)</li>
      </ul>

      <h2 className="text-2xl font-bold mb-2">4. Creator Terms</h2>
      <h3 className="text-xl font-semibold mb-2">4.1 Content Submissions</h3>
      <p className="mb-2">As a Creator, you may submit content for available campaigns. By submitting content, you:</p>
      <ul className="list-disc list-inside mb-4 space-y-1">
        <li>Represent that you own or have all necessary rights to the content</li>
        <li>Grant Eyam and the Business a license to use, display, and distribute the content</li>
        <li>Agree that submissions are subject to review and may be approved or rejected at the Business&apos;s discretion</li>
        <li>Acknowledge that rejected submissions will not receive compensation</li>
        <li>Agree to include proper disclosure hashtags (#EyamAd or #PaidEyamAd) as required by South African advertising standards</li>
      </ul>

      <h3 className="text-xl font-semibold mb-2">4.2 Social Media Requirements</h3>
      <p className="mb-2">For creator campaigns, you must:</p>
      <ul className="list-disc list-inside mb-4 space-y-1">
        <li>Post approved content to the specified social media platforms (TikTok, Instagram, Facebook, etc.)</li>
        <li>Provide accurate links to your posts within the required timeframe</li>
        <li>Keep posts live for the duration specified in the campaign terms</li>
        <li>Not artificially inflate views, likes, or engagement through bots, purchased services, or click farms</li>
        <li>Add your unique creative spin to content (no raw reposting of business-generated content without modification)</li>
      </ul>

      <h3 className="text-xl font-semibold mb-2">4.3 Compensation</h3>
      <p className="mb-2">Compensation for campaigns is determined as follows:</p>
      <ul className="list-disc list-inside mb-4 space-y-1">
        <li>Referral Earnings: R100 per successful referral (when a referred user completes signup and pays for a discovery listing or launches a campaign)</li>
        <li>View-Based Earnings: R20 per 1,000 verified views on approved content</li>
        <li>Performance Bonuses: Additional bonuses for high-performing videos as determined by Eyam</li>
        <li>Campaign Payouts: For business-generated content distribution, creators receive compensation from the 40% allocation of campaign budgets</li>
      </ul>
      <p className="mb-4">
        Payouts are processed through secure South African payment providers and
        are subject to identity verification and compliance with FICA
        requirements. Minimum payout threshold: R50. Payout timeline: 7-14 days
        after approval and validation. We reserve the right to withhold payment
        if we detect fraudulent activity, Terms violations, or failure to comply
        with TikTok/Facebook/Instagram platform guidelines.
      </p>

      <h3 className="text-xl font-semibold mb-2">4.4 Withdrawal and Cancellation</h3>
      <p className="mb-4">
        You may withdraw a pending submission at any time before approval. Once a
        submission is approved, you are committed to fulfilling the campaign
        requirements. If a campaign is deactivated by the Business, pending
        submissions will be canceled, and approved submissions will be finalized
        with payouts based on performance to date.
      </p>

      <h2 className="text-2xl font-bold mb-2">5. Business Terms</h2>
      <h3 className="text-xl font-semibold mb-2">5.1 Campaign Creation</h3>
      <p className="mb-2">Businesses may create campaigns subject to platform guidelines. By creating a campaign, you agree to:</p>
      <ul className="list-disc list-inside mb-4 space-y-1">
        <li>Fund the specified budget according to our 40/60 allocation model</li>
        <li>Honor the compensation terms for approved submissions</li>
        <li>Provide clear campaign briefs and requirements</li>
        <li>Review creator submissions in a timely manner (within 7 days)</li>
      </ul>

      <h3 className="text-xl font-semibold mb-2">5.2 Discovery Listings</h3>
      <ul className="list-disc list-inside mb-4 space-y-1">
        <li>Free Listing: Businesses can claim a free basic directory listing on Eyam (30-day trial, then R349/month for continued active listing)</li>
        <li>Paid Listing: R349/month for priority placement, verified badge, real-time analytics, and ability to respond to reviews</li>
        <li>Campaign Bonus: Launch a campaign from R1,500 and receive a free paid listing included</li>
      </ul>

      <h3 className="text-xl font-semibold mb-2">5.3 Content Review</h3>
      <p className="mb-4">Businesses are responsible for reviewing and approving or rejecting Creator submissions in a timely manner. Approval grants the Creator permission to post the content and triggers the tracking period for payout calculation.</p>

      <h3 className="text-xl font-semibold mb-2">5.4 Budget and Payment</h3>
      <p className="mb-2">Campaign budgets operate on our unified model:</p>
      <ul className="list-disc list-inside mb-4 space-y-1">
        <li>Minimum Campaign: R1,500</li>
        <li>40% Allocation (R600): Funds 3 authentic UGC videos from creators</li>
        <li>60% Allocation (R900): Funds verified distribution at R20 per 1,000 views (~45,000 views)</li>
        <li>Budgets must be funded in advance via secure payment methods</li>
        <li>Campaigns will be automatically deactivated when the budget is exhausted or the expiration date is reached</li>
        <li>Businesses may manually deactivate campaigns at any time; approved submissions will be honored</li>
      </ul>

      <h3 className="text-xl font-semibold mb-2">5.5 Unified Tracking</h3>
      <p className="mb-2">Businesses receive access to Eyam&apos;s unified dashboard showing:</p>
      <ul className="list-disc list-inside mb-4 space-y-1">
        <li>Total verified views</li>
        <li>Click-through rates (call, WhatsApp, website)</li>
        <li>Conversion actions</li>
        <li>Cost per verified click</li>
        <li>ROI estimates</li>
      </ul>

      <h2 className="text-2xl font-bold mb-2">6. Content Guidelines</h2>
      <p className="mb-2">All content submitted through the Platform must comply with the following guidelines. You may not submit content that:</p>
      <ul className="list-disc list-inside mb-4 space-y-1">
        <li>Infringes on any third party&apos;s intellectual property rights</li>
        <li>Contains defamatory, obscene, or offensive material</li>
        <li>Promotes illegal activities or violates any applicable South African laws</li>
        <li>Contains malware, spam, or malicious content</li>
        <li>Misrepresents your identity or affiliation</li>
        <li>Violates the terms of service of any social media platform (TikTok, Facebook, Instagram, WhatsApp, etc.)</li>
        <li>Contains false or misleading claims about products or services</li>
        <li>Violates the Consumer Protection Act (CPA) or Advertising Standards Authority (ASA) codes of South Africa</li>
        <li>Contains watermarks from third-party editing apps (CapCut, InShot, etc.)</li>
      </ul>

      <h2 className="text-2xl font-bold mb-2">7. Intellectual Property</h2>
      <h3 className="text-xl font-semibold mb-2">7.1 Platform Ownership</h3>
      <p className="mb-4">The Eyam Platform, including all software, designs, text, images, and other content provided by us, is owned by Eyam and protected by South African intellectual property laws, including the Copyright Act 98 of 1978 and the Trade Marks Act 194 of 1993. You may not copy, modify, distribute, or create derivative works without our express written permission.</p>
      <h3 className="text-xl font-semibold mb-2">7.2 Creator Content</h3>
      <p className="mb-2">Creators retain ownership of their original content. By submitting content, you grant Eyam and the applicable Business a non-exclusive, worldwide, royalty-free license to use, reproduce, modify, adapt, publish, translate, and display the content for the purposes of:</p>
      <ul className="list-disc list-inside mb-4 space-y-1">
        <li>Operating the Platform</li>
        <li>Fulfilling campaign requirements</li>
        <li>Marketing and promotional purposes related to Eyam</li>
        <li>The duration specified in the campaign terms</li>
      </ul>
      <h3 className="text-xl font-semibold mb-2">7.3 Business Content</h3>
      <p className="mb-4">Businesses retain ownership of their uploaded content. By distributing content through Eyam, you grant Eyam and participating Creators a limited license to share, distribute, and add creative modifications to the content for campaign purposes only.</p>

      <h2 className="text-2xl font-bold mb-2">8. Prohibited Activities</h2>
      <p className="mb-2">You agree not to engage in any of the following prohibited activities:</p>
      <ul className="list-disc list-inside mb-4 space-y-1">
        <li>Creating multiple accounts or using false identities</li>
        <li>Artificially inflating engagement metrics through bots, purchased views, click farms, or engagement pods</li>
        <li>Circumventing or manipulating the Platform&apos;s tracking, attribution, or payout systems</li>
        <li>Harassing, threatening, or intimidating other users</li>
        <li>Attempting to gain unauthorized access to the Platform or other users&apos; accounts</li>
        <li>Using the Platform for any illegal purpose or in violation of South African laws, including POPIA</li>
        <li>Scraping, data mining, or using automated tools to access the Platform without authorization</li>
        <li>Interfering with the proper operation of the Platform</li>
        <li>Uploading content with third-party watermarks (CapCut, InShot, VN, etc.)</li>
        <li>Posting content without proper disclosure (#EyamAd or #PaidEyamAd)</li>
        <li>Engaging in fraudulent click or view schemes</li>
      </ul>

      <h2 className="text-2xl font-bold mb-2">9. Disclaimers</h2>
      <p className="mb-4">THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.</p>
      <p className="mb-2">WE DO NOT WARRANT THAT:</p>
      <ul className="list-disc list-inside mb-4 space-y-1">
        <li>THE PLATFORM WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE</li>
        <li>ANY SPECIFIC RESULTS, EARNINGS, OR OUTCOMES WILL BE ACHIEVED</li>
        <li>CONTENT WILL ACHIEVE ANY SPECIFIC NUMBER OF VIEWS, CLICKS, OR CONVERSIONS</li>
      </ul>
      <p className="mb-4">Actual earnings and results depend on various factors including content quality, audience engagement, campaign availability, market conditions, and compliance with platform guidelines. We do not guarantee any minimum earnings or business outcomes.</p>

      <h2 className="text-2xl font-bold mb-2">10. Limitation of Liability</h2>
      <p className="mb-4">TO THE MAXIMUM EXTENT PERMITTED BY SOUTH AFRICAN LAW, EYAM AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, BUSINESS OPPORTUNITIES, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF THE PLATFORM.</p>
      <p className="mb-4">OUR TOTAL AGGREGATE LIABILITY TO YOU FOR ANY CLAIM ARISING OUT OF OR RELATING TO THESE TERMS OR YOUR USE OF THE PLATFORM SHALL NOT EXCEED THE AMOUNTS PAID TO YOU OR BY YOU THROUGH THE PLATFORM IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR R1,000 (ONE THOUSAND RAND), WHICHEVER IS GREATER.</p>
      <p className="mb-2">NOTHING IN THESE TERMS SHALL EXCLUDE OR LIMIT OUR LIABILITY FOR:</p>
      <ul className="list-disc list-inside mb-4 space-y-1">
        <li>DEATH OR PERSONAL INJURY CAUSED BY OUR NEGLIGENCE</li>
        <li>FRAUD OR FRAUDULENT MISREPRESENTATION</li>
        <li>ANY MATTER FOR WHICH LIABILITY CANNOT BE EXCLUDED OR LIMITED UNDER SOUTH AFRICAN LAW, INCLUDING THE CONSUMER PROTECTION ACT 68 OF 2008</li>
      </ul>

      <h2 className="text-2xl font-bold mb-2">11. Indemnification</h2>
      <p className="mb-4">You agree to indemnify, defend, and hold harmless Eyam and its officers, directors, employees, agents, and affiliates from and against any claims, liabilities, damages, losses, demands, actions, and expenses (including reasonable attorneys&apos; fees and costs) arising out of or related to your use of the Platform, your content or submissions, your violation of these Terms, your infringement of any third-party rights, or your violation of any South African laws or regulations.</p>

      <h2 className="text-2xl font-bold mb-2">12. Termination</h2>
      <h3 className="text-xl font-semibold mb-2">12.1 By Eyam</h3>
      <p className="mb-2">We may suspend or terminate your account at any time for any reason, including but not limited to:</p>
      <ul className="list-disc list-inside mb-4 space-y-1">
        <li>Violation of these Terms</li>
        <li>Fraudulent or illegal activity</li>
        <li>Extended inactivity (12+ months)</li>
        <li>Request by law enforcement or regulatory authorities</li>
      </ul>
      <h3 className="text-xl font-semibold mb-2">12.2 By You</h3>
      <p className="mb-4">You may terminate your account at any time by contacting us at legal@eyam.finance or through the Platform settings.</p>
      <h3 className="text-xl font-semibold mb-2">12.3 Effect of Termination</h3>
      <p className="mb-2">Upon termination:</p>
      <ul className="list-disc list-inside mb-4 space-y-1">
        <li>Your right to use the Platform will immediately cease</li>
        <li>Pending payouts for approved and finalized submissions will be processed according to normal procedures (7-14 days)</li>
        <li>Outstanding campaign budgets will be handled according to campaign status</li>
        <li>Provisions of these Terms that by their nature should survive termination (including intellectual property, disclaimers, limitation of liability, indemnification, and dispute resolution) will remain in effect</li>
      </ul>

      <h2 className="text-2xl font-bold mb-2">13. Privacy and Data Protection</h2>
      <p className="mb-4">Your use of the Platform is also governed by our Privacy Policy, which explains how we collect, use, and protect your personal information in compliance with POPIA 4 of 2013. By using Eyam, you consent to the collection and processing of your personal information as described in our Privacy Policy.</p>

      <h2 className="text-2xl font-bold mb-2">14. Dispute Resolution</h2>
      <h3 className="text-xl font-semibold mb-2">14.1 Informal Resolution</h3>
      <p className="mb-4">We encourage you to contact us first at legal@eyam.finance to resolve any disputes or concerns informally. We will make good faith efforts to resolve the dispute within 30 days.</p>
      <h3 className="text-xl font-semibold mb-2">14.2 Formal Dispute Resolution</h3>
      <p className="mb-2">If informal resolution fails, any disputes arising out of or relating to these Terms or your use of the Platform shall be resolved:</p>
      <ul className="list-disc list-inside mb-4 space-y-1">
        <li>Option A: Through mediation in accordance with the rules of the Arbitration Foundation of Southern Africa (AFSA), followed by arbitration if mediation is unsuccessful</li>
        <li>Option B: Through the courts of South Africa</li>
      </ul>
      <p className="mb-4">You agree to submit to the exclusive jurisdiction of the courts of South Africa for any legal proceedings.</p>
      <h3 className="text-xl font-semibold mb-2">14.3 Class Action Waiver</h3>
      <p className="mb-4">TO THE EXTENT PERMITTED BY SOUTH AFRICAN LAW, ANY DISPUTES SHALL BE RESOLVED ON AN INDIVIDUAL BASIS AND NOT AS PART OF A CLASS ACTION OR REPRESENTATIVE PROCEEDING. HOWEVER, THIS WAIVER DOES NOT APPLY WHERE PROHIBITED BY THE CONSUMER PROTECTION ACT.</p>

      <h2 className="text-2xl font-bold mb-2">15. Governing Law</h2>
      <p className="mb-2">These Terms shall be governed by and construed in accordance with the laws of the Republic of South Africa, including but not limited to:</p>
      <ul className="list-disc list-inside mb-4 space-y-1">
        <li>The Consumer Protection Act 68 of 2008</li>
        <li>The Protection of Personal Information Act (POPIA) 4 of 2013</li>
        <li>The Electronic Communications and Transactions Act 25 of 2002</li>
        <li>The Copyright Act 98 of 1978</li>
        <li>The Common Law of Contract</li>
      </ul>
      <p className="mb-4">Any legal proceedings shall be conducted in the courts of South Africa.</p>

      <h2 className="text-2xl font-bold mb-2">16. Severability</h2>
      <p className="mb-4">If any provision of these Terms is found to be unenforceable, invalid, or illegal under South African law, that provision shall be limited or eliminated to the minimum extent necessary so that these Terms shall otherwise remain in full force and effect and enforceable. The remaining provisions will continue in full force and effect.</p>

      <h2 className="text-2xl font-bold mb-2">17. Force Majeure</h2>
      <p className="mb-4">Eyam shall not be liable for any failure or delay in performing its obligations under these Terms due to causes beyond its reasonable control, including but not limited to: acts of God, war, terrorism, civil unrest, government regulations, natural disasters, power outages, internet service disruptions, or pandemics.</p>

      <h2 className="text-2xl font-bold mb-2">18. Assignment</h2>
      <p className="mb-4">You may not assign or transfer your rights or obligations under these Terms without our prior written consent. Eyam may assign these Terms in whole or in part in connection with a merger, acquisition, sale of assets, or other business transaction.</p>

      <h2 className="text-2xl font-bold mb-2">19. Entire Agreement</h2>
      <p className="mb-4">These Terms, together with our Privacy Policy and any additional terms incorporated by reference, constitute the entire agreement between you and Eyam regarding your use of the Platform and supersede all prior or contemporaneous agreements, understandings, or representations, whether written or oral.</p>

      <h2 className="text-2xl font-bold mb-2">20. Contact Information</h2>
      <p className="mb-1">Eyam Investment Technologies (Pty) Ltd</p>
      <p className="mb-1">Email: legal@eyam.finance</p>
      <p className="mb-1">Phone: 0872652055</p>
      <p className="mb-1">WhatsApp: 0822534873</p>
      <p className="mb-1">Website: eyam.finance</p>
      <p className="mb-4">Registered Address: The Boulders, 1 Sunningdale Avenue, Sunnyridge, East London, South Africa</p>
      <p className="mb-4">Business Hours: Monday-Friday, 8:00 AM - 5:00 PM (South African Standard Time)</p>

      <h2 className="text-2xl font-bold mb-2">21. Additional Provisions for South African Users</h2>
      <h3 className="text-xl font-semibold mb-2">21.1 Consumer Protection Act (CPA) Rights</h3>
      <p className="mb-4">If you are a "consumer" as defined in the Consumer Protection Act 68 of 2008, you have certain statutory rights that cannot be excluded or limited by these Terms. Nothing in these Terms is intended to exclude or limit any rights granted to you under the CPA.</p>
      <h3 className="text-xl font-semibold mb-2">21.2 Cooling-Off Period</h3>
      <p className="mb-4">For certain transactions, you may have a cooling-off period as provided by the Consumer Protection Act. Contact us for more information.</p>
      <h3 className="text-xl font-semibold mb-2">21.3 Language</h3>
      <p className="mb-4">These Terms are provided in English. If you require these Terms in another official South African language, please contact us.</p>
      <h3 className="text-xl font-semibold mb-2">21.4 Tax Obligations</h3>
      <p className="mb-4">Creators are responsible for declaring their Eyam earnings to the South African Revenue Service (SARS) and paying any applicable taxes. Eyam does not provide tax advice. Consult a tax professional for guidance on your obligations.</p>

      <h2 className="text-2xl font-bold mb-2">22. Integration with Third-Party Platforms</h2>
      <h3 className="text-xl font-semibold mb-2">22.1 Third-Party API Usage</h3>
      <p className="mb-2">Eyam utilizes third-party application interfaces (APIs), including services provided by TikTok, Instagram, Facebook, WhatsApp, and other social media platforms, to facilitate content distribution, tracking, and campaign management. By using Eyam to draft, submit, or deploy content, you acknowledge and agree that:</p>
      <ul className="list-disc list-inside mb-4 space-y-1">
        <li>Your use of these third-party platforms is governed by their respective terms of service, community guidelines, and acceptable use policies</li>
        <li>Eyam is not responsible for the operation, content, privacy practices, or policies of any third-party platform</li>
        <li>Changes to third-party platform policies or API availability may affect Eyam&apos;s functionality, and we reserve the right to modify or discontinue integrations without prior notice</li>
        <li>Where you connect Facebook, Eyam may access your profile details and details of your posts/videos, including values such as views, likes, and comments, for campaign analytics and reporting</li>
      </ul>

      <h3 className="text-xl font-semibold mb-2">22.7 Data Deletion Requests</h3>
      <p className="mb-2">If you want Eyam to delete your personal data, you may request deletion by emailing <a href="mailto:info@eyam.finance">info@eyam.finance</a>.</p>
      <p className="mb-4">For security and verification, you must send the request from the same email address you used to sign up on Eyam. In your email, clearly state that you are requesting data deletion and include any account details needed to identify your profile.</p>

      <h3 className="text-xl font-semibold mb-2">22.2 TikTok Compliance Requirements</h3>
      <p className="mb-2">Specifically regarding TikTok integration, you agree to comply with:</p>
      <ul className="list-disc list-inside mb-4 space-y-1">
        <li>TikTok Terms of Service (https://www.tiktok.com/legal/terms-of-service)</li>
        <li>TikTok Community Guidelines (https://www.tiktok.com/community-guidelines)</li>
        <li>TikTok Advertising Policies (https://www.tiktok.com/legal/advertising-policies)</li>
        <li>TikTok Creator Marketplace Terms (if applicable)</li>
      </ul>

      <h3 className="text-xl font-semibold mb-2">22.3 Creator Obligations for TikTok Content</h3>
      <p className="mb-2">When creating or distributing content via TikTok through Eyam, you must:</p>
      <ul className="list-disc list-inside mb-4 space-y-1">
        <li>Include proper disclosure hashtags (#EyamAd or #PaidEyamAd) as required by TikTok&apos;s commercial content policies and South African ASA codes</li>
        <li>Add your unique creative spin to business-provided content (no raw reposting without modification) to comply with TikTok&apos;s originality requirements</li>
        <li>Remove watermarks from third-party editing apps (CapCut, InShot, VN, etc.) before uploading to TikTok</li>
        <li>Not engage in artificial engagement inflation (bots, purchased views, click farms) which violates both TikTok policies and these Terms</li>
        <li>Ensure all content complies with TikTok&apos;s prohibited content categories and South African laws</li>
      </ul>

      <h3 className="text-xl font-semibold mb-2">22.4 Platform Policy Changes</h3>
      <p className="mb-2">Third-party platforms may update their terms, guidelines, or API access at any time. You are responsible for:</p>
      <ul className="list-disc list-inside mb-4 space-y-1">
        <li>Staying informed of policy changes on platforms you use through Eyam</li>
        <li>Adjusting your content and posting practices to remain compliant</li>
        <li>Understanding that non-compliance with third-party policies may result in content removal, account suspension, payout ineligibility, or Eyam account suspension/termination for repeated violations</li>
      </ul>

      <h3 className="text-xl font-semibold mb-2">22.5 Eyam&apos;s Role and Limitations</h3>
      <p className="mb-2">Eyam acts as a facilitator between Creators, Businesses, and third-party platforms. We:</p>
      <ul className="list-disc list-inside mb-4 space-y-1">
        <li>Provide guidelines and tools to help you comply with third-party policies</li>
        <li>May reject or request modification of content that appears to violate third-party terms</li>
        <li>Do not guarantee that content distributed via Eyam will remain live, compliant, or monetizable on third-party platforms</li>
        <li>Are not liable for actions taken by third-party platforms against your account or content</li>
      </ul>

      <h3 className="text-xl font-semibold mb-2">22.6 Indemnification for Third-Party Violations</h3>
      <p className="mb-4">You agree to indemnify and hold harmless Eyam from any claims, penalties, or damages arising from your violation of a third-party platform&apos;s terms of service, including but not limited to TikTok account suspensions or bans, content takedowns or demonetization, and legal claims related to copyright, defamation, or advertising violations on third-party platforms. This indemnification obligation survives termination of your Eyam account.</p>

      <p className="font-semibold">Content is Currency. Sebenza. Vuka Uzenzele.</p>
      <p className="text-sm text-gray-600 mt-2">© 2026 Eyam (Pty) Ltd. All rights reserved.</p>
    </Card>
  );
}

export default TermsAndConditions;
